/**
 * Tasklist row shaping shared by getchecklist pages and the single-row
 * getTask patch (NXTGEN-8043 F3/F4). No imports so node --test can load it.
 */

const BLOCKED_CUSTOM_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Recon amounts getTask and getchecklist send only when a bot summary exists.
 * An omitted key means the task has no bot recon. Do not keep the previous value.
 */
export const BOT_RECON_KEYS = [
  'bot_recon_summary',
  'bot_recon_schedule_amount',
  'bot_recon_explained_difference',
  'bot_recon_unexplained_difference',
];

/**
 * Kept from the previous row when this getTask body omits them.
 * Bot recon keys are not in this list: omission is the latest value.
 */
const GET_TASK_MISSING_COMPUTED_KEYS = [
  'bot_details',
  'bot_analysis_detail',
];

/**
 * @param {object} task
 * @param {string} key
 * @returns {boolean}
 */
export function taskHasOwnKey(task, key) {
  const computed = task && task.__computed && typeof task.__computed === 'object' ? task.__computed : {};
  return Object.prototype.hasOwnProperty.call(computed, key)
    || Object.prototype.hasOwnProperty.call(task || {}, key);
}
const GET_TASK_MISSING_ROW_KEYS = ['blocked'];

/** Closed information requests. Same check as TasksLandingPage's stage === 2 filter. */
const INFO_REQUEST_COMPLETED_STAGE = 2;

/**
 * total = linked requests. completed = stage === 2.
 * Uses the server counts when the list sent them. getTask still returns the
 * array, so a patched row counts that array with the same rule.
 * @param {object} computed
 * @returns {{ total: number, completed: number }}
 */
export function infoManagerRequestCountsFromComputed(computed) {
  const counts = computed && computed.infoManagerRequestCounts;
  if (counts && typeof counts === 'object') {
    return {
      total: Number(counts.total) || 0,
      completed: Number(counts.completed) || 0,
    };
  }
  const list = computed && Array.isArray(computed.infoManagerRequests)
    ? computed.infoManagerRequests
    : [];
  return {
    total: list.length,
    completed: list.filter((request) => request && request.stage === INFO_REQUEST_COMPLETED_STAGE).length,
  };
}

function idString(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return idString(value._id || value.$oid || value.id);
  return String(value);
}

function flattenCustomFields(item, customColumns) {
  const cf = item.customfields;
  if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
    Object.assign(customColumns, cf);
    return { ...item, ...cf };
  }
  if (Array.isArray(cf)) {
    const flatCF = Object.assign({}, ...cf);
    Object.assign(customColumns, flatCF);
    return { ...item, ...flatCF };
  }
  return { ...item };
}

/**
 * Copy __computed fields onto the task, as the list and detail views read them.
 * @param {object} item raw getchecklist / getTask record
 * @param {object} [customColumns] collects flattened custom field keys
 * @returns {object}
 */
export function mapChecklistRecord(item, customColumns = {}) {
  const task = flattenCustomFields(item || {}, customColumns);
  const computed = task.__computed || {};
  task.threshold = computed.threshold ?? 'NA';
  task.locationName = computed.locationName || '';
  task.alltaskassigned = computed.alltaskassigned
    ?? ((task.preparer || []).length > 0 && (task.approver || []).length > 0);
  task.__tbtotals = computed.tbtotals || { total: 0, items: [] };
  task.__tmIndicator = computed.tmIndicator || null;
  task.infoManagerRequestCounts = infoManagerRequestCountsFromComputed(computed);
  task.bot_details = computed.bot_details || {};
  task.bot_analysis_detail = computed.bot_analysis_detail || {};
  // Raw annexure rows are off unless the request asks for them. Do not invent [].
  if (computed.bot_recon_summary !== undefined) {
    task.bot_recon_summary = computed.bot_recon_summary;
  }
  if (task.__computed && computed.bot_recon_schedule_amount !== undefined) {
    task.bot_recon_schedule_amount = computed.bot_recon_schedule_amount || 0;
  }
  if (task.__computed && computed.bot_recon_explained_difference !== undefined) {
    task.bot_recon_explained_difference = computed.bot_recon_explained_difference || 0;
  }
  if (task.__computed && computed.bot_recon_unexplained_difference !== undefined) {
    task.bot_recon_unexplained_difference = computed.bot_recon_unexplained_difference || 0;
  }
  return task;
}

/**
 * Map a getchecklist page.
 * @param {object[]} records
 * @returns {{ rows: object[], customColumns: object }}
 */
export function mapChecklistRecords(records) {
  const customColumns = {};
  const rows = (Array.isArray(records) ? records : []).map(
    (item) => mapChecklistRecord(item, customColumns)
  );
  return { rows, customColumns };
}

/** Add schema columns that no row carried, as empty values. */
export function addSchemaCustomColumns(customColumns, customColumnSchema) {
  (Array.isArray(customColumnSchema) ? customColumnSchema : []).forEach((entry) => {
    const key = entry?.columnName || entry?.key;
    if (
      key
      && !BLOCKED_CUSTOM_KEYS.has(key)
      && !Object.prototype.hasOwnProperty.call(customColumns, key)
    ) {
      customColumns[key] = '';
    }
  });
  return customColumns;
}

/**
 * Build the list row from a v3 getTask record, keeping fields getTask does not return.
 * @param {object} existingRow current list row
 * @param {object} freshRecord getTask data
 * @returns {object}
 */
export function mergeGetTaskRow(existingRow, freshRecord) {
  const existing = existingRow || {};
  const fresh = freshRecord || {};
  const existingComputed = existing.__computed || {};
  const freshComputed = fresh.__computed || {};
  const computed = { ...freshComputed };
  GET_TASK_MISSING_COMPUTED_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(freshComputed, key)
      && Object.prototype.hasOwnProperty.call(existingComputed, key)) {
      computed[key] = existingComputed[key];
    }
  });
  const row = mapChecklistRecord({ ...fresh, __computed: computed });
  BOT_RECON_KEYS.forEach((key) => {
    if (!taskHasOwnKey(fresh, key)) delete row[key];
  });
  GET_TASK_MISSING_ROW_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(fresh, key)
      && Object.prototype.hasOwnProperty.call(existing, key)) {
      row[key] = existing[key];
    }
  });
  return row;
}

/**
 * Replace one row by id. Order and the other row objects are unchanged.
 * @returns {{ list: object[], index: number }} index is -1 when the id is not in the list
 */
export function replaceTaskRow(list, row) {
  const source = Array.isArray(list) ? list : [];
  const wanted = idString(row?._id);
  const index = wanted ? source.findIndex((item) => idString(item?._id) === wanted) : -1;
  if (index === -1) return { list: source, index };
  const next = source.slice();
  next[index] = row;
  return { list: next, index };
}

/** Drop one row by id. */
export function removeTaskRow(list, taskId) {
  const source = Array.isArray(list) ? list : [];
  const wanted = idString(taskId);
  return source.filter((item) => idString(item?._id) !== wanted);
}

/**
 * A single-row patch is safe only when the task has no dependency links.
 * A stage change on a blocker flips `blocked` on other rows, and getTask
 * does not return `blocked`. Unknown links (not loaded) count as linked.
 * @param {object} task task with getdetails blocking / blockedBy / relates
 */
export function canPatchTaskRow(task) {
  if (!task) return false;
  const lists = [task.blocking, task.blockedBy, task.relates];
  return lists.every((value) => Array.isArray(value) && value.length === 0);
}

/** Edit payload that adds or removes dependency links. */
export function editTouchesTaskDependencies(addTaskData) {
  const links = addTaskData?.links;
  const unlinked = addTaskData?.unlinkWaitingOn;
  return (Array.isArray(links) && links.length > 0)
    || (Array.isArray(unlinked) && unlinked.length > 0);
}
